import React from "react"

import { breakdownTotals, breakdownTree, costBreakdowns, rollUpBreakdowns, lineCalculations, roundNumber, formatNumber, formatCoef } from "./helpers"
import { COST_CATEGORIES, enabledCategoriesFor, CATEGORY_ICONS, CATEGORY_COLORS } from "./qlines/shared/category_styles"

const LABOR_UNITS = ["h", "hr", "hrs", "heure", "heures"]
const NON_COST_TYPES = ["subtotal", "section", "break", "descriptive", "group", "blueprint", "template", "import_from_csv"]

const categoryFor = (line) => line.cost_category || "other"

const emptyBucket = () => ({ purchase: 0, sale: 0, count: 0, hours: 0 })

const lineSaleAndCost = (line) => {
  const calc = lineCalculations(line)
  return {
    sale:     (calc.total || 0) - (calc.discount || 0),
    purchase: calc.purchase || 0,
  }
}

const addBreakdownsToBuckets = (line, buckets) => {
  const lineQty    = parseFloat(line.quantity) || 0
  const breakdowns = costBreakdowns(line.qline_breakdowns)
  // Distribute the line's sale price proportionally to each breakdown's sale value.
  const breakdownsTotalSale = breakdowns.reduce((acc, b) => {
    const qty = parseFloat(b.quantity)   || 0
    const up  = parseFloat(b.unit_price) || 0
    return acc + qty * up
  }, 0) * lineQty
  const { sale: lineSale } = lineSaleAndCost(line)

  breakdowns.forEach((b) => {
    const category = b.cost_category || "other"
    const bucket   = buckets[category]
    if (!bucket) return

    const qty       = parseFloat(b.quantity)       || 0
    const pa        = parseFloat(b.purchase_price) || 0
    const up        = parseFloat(b.unit_price)     || 0
    const purchase  = qty * pa * lineQty
    const grossSale = qty * up * lineQty
    // Apply the line-level discount weight: scale gross to actual line sale.
    const sale = breakdownsTotalSale > 0 ? (grossSale * lineSale / breakdownsTotalSale) : grossSale

    bucket.purchase += purchase
    bucket.sale     += sale
    bucket.count    += 1
    if (category === "labor" && LABOR_UNITS.includes(String(b.unit || "").trim().toLowerCase())) {
      bucket.hours += qty * lineQty
    }
  })
}

const addLineToBuckets = (line, buckets) => {
  const { sale, purchase } = lineSaleAndCost(line)
  if (sale === 0 && purchase === 0) return

  const bucket = buckets[categoryFor(line)]
  if (!bucket) return

  bucket.purchase += purchase
  bucket.sale     += sale
  bucket.count    += 1
  if (categoryFor(line) === "labor" && LABOR_UNITS.includes(String(line.unit || "").trim().toLowerCase())) {
    bucket.hours += parseFloat(line.quantity) || 0
  }
}

const computeBuckets = (quote, categories) => {
  const buckets = {}
  ;(categories || COST_CATEGORIES).forEach(c => (buckets[c] = emptyBucket()))

  quote.qlines.forEach((line) => {
    if (line.destroyed || line.option) return
    if (NON_COST_TYPES.includes(line.type)) return

    const breakdowns = (line.qline_breakdowns || []).filter(b => !b._destroy)
    if (breakdowns.length > 0) {
      addBreakdownsToBuckets(line, buckets)
    } else {
      addLineToBuckets(line, buckets)
    }
  })

  return buckets
}

const totalsOf = (buckets) => {
  return Object.values(buckets).reduce((acc, b) => ({
    purchase: acc.purchase + b.purchase,
    sale:     acc.sale     + b.sale,
    count:    acc.count    + b.count,
    hours:    acc.hours    + b.hours,
  }), emptyBucket())
}

const marginPct      = (sale, purchase) => (sale > 0 ? 100 * (sale - purchase) / sale : 0)

// Recompute the qlines by applying an overhead coef + sale coef to all
// (sub-)lines of the same category. Returns the new array; the decision
// to write it into the quote state belongs to the caller.
const computeNewQlinesForCategory = (quote, category, overheadCoef, coefVente) => {
  if (!isFinite(coefVente) || coefVente <= 0) return quote.qlines
  const revientMultiplier = 1 + overheadCoef

  return quote.qlines.map((line) => {
    if (line.destroyed || line.option) return line
    if (NON_COST_TYPES.includes(line.type)) return line

    // Case A — the line has live breakdowns: we inspect each
    // breakdown by ITS own cost_category (a service can mix
    // labor / equipment / materials). Consistent with
    // addBreakdownsToBuckets on the display side.
    const breakdowns = line.qline_breakdowns || []
    const liveBreakdowns = breakdowns.filter(b => !b._destroy)
    if (liveBreakdowns.length > 0) {
      const tree = breakdownTree(breakdowns)
      let touched = false
      const rawBreakdowns = breakdowns.map((b) => {
        if (b._destroy) return b
        if (tree.childrenOf(b).length > 0) return b
        if ((b.cost_category || "other") !== category) return b
        const pa = parseFloat(b.purchase_price) || 0
        if (pa <= 0) return b
        touched = true
        const revient = pa * revientMultiplier
        return { ...b, unit_price: roundNumber(revient * coefVente) }
      })
      if (!touched) return line
      const newBreakdowns = rollUpBreakdowns(rawBreakdowns)
      const sums = breakdownTotals(newBreakdowns)
      return {
        ...line,
        qline_breakdowns: newBreakdowns,
        unit_price:       roundNumber(sums.unit_price),
        purchase_price:   roundNumber(sums.purchase_price),
      }
    }

    // Case B — line without breakdown: filter on its own category.
    if (categoryFor(line) !== category) return line
    const pa = parseFloat(line.purchase_price) || 0
    if (pa <= 0) return line
    const revient = pa * revientMultiplier
    return { ...line, unit_price: roundNumber(revient * coefVente) }
  })
}

const parseCoef = value => {
  const n = parseFloat(String(value).replace(",", "."))
  return Number.isFinite(n) && n >= 0 ? n : 0
}

// Signed variant: allows negative values. Used for the
// overhead coef which can represent a credit / discount (typically < 0).
const parseCoefSigned = value => {
  const n = parseFloat(String(value).replace(",", "."))
  return Number.isFinite(n) ? n : 0
}

export default class CostBreakdownTable extends React.Component {
  constructor(props) {
    super(props)
    // `targets`   : sale coef TYPED by the user (per category).
    // `fgTargets` : overhead coef TYPED by the user (per category).
    //
    // The local state ONLY stores what the user has entered but
    // not yet applied. The APPLIED (persisted) value lives on the
    // quote (`overhead_coef_overrides` / `sale_coef_overrides`) and is
    // broadcast to all quote lines so they update
    // their displayed cost price. On clicking Apply we update
    // the quote and clear the state ↦ the inputs revert to the
    // applied value and the button is automatically disabled again.
    this.state = { targets: {}, fgTargets: {}, kTargets: {} }
  }

  // Sale coefficient and K say the same thing two ways: the one being typed
  // takes over and clears its twin.
  setTarget(category, value) {
    const kTargets = { ...this.state.kTargets }; delete kTargets[category]
    this.setState({ targets: { ...this.state.targets, [category]: value }, kTargets })
  }

  setK(category, value) {
    const targets = { ...this.state.targets }; delete targets[category]
    this.setState({ kTargets: { ...this.state.kTargets, [category]: value }, targets })
  }

  // Correcting the overhead keeps the commercial margin and lets K rise. A K
  // just typed is frozen into a sale coefficient first, or the decision taken
  // would be wiped by the next correction.
  setFgTarget(category, value) {
    const fgTargets = { ...this.state.fgTargets, [category]: value }
    if (this.state.kTargets[category] === undefined) { this.setState({ fgTargets }); return }

    const { saleCoef } = this.resolveFor(category)
    const kTargets = { ...this.state.kTargets }; delete kTargets[category]
    this.setState({
      fgTargets,
      kTargets,
      targets: { ...this.state.targets, [category]: saleCoef > 0 ? formatCoef(saleCoef) : "" },
    })
  }

  // Resolved once for both the display and the application, so the screen
  // cannot announce one price and the click apply another.
  resolveFor(category) {
    const appliedFG    = this.appliedFGFor(category)
    const appliedVente = this.appliedVenteFor(category)
    // An emptied field means "untouched", never zero.
    const typed = map => {
      const value = map[category]
      return (value === undefined || value === "") ? undefined : value
    }
    const typedFg   = typed(this.state.fgTargets)
    const typedSale = typed(this.state.targets)
    const typedK    = typed(this.state.kTargets)

    const overheadCoef = typedFg !== undefined ? parseCoefSigned(typedFg) : appliedFG
    const divisor      = 1 + overheadCoef
    const saleCoef     = typedK    !== undefined ? (divisor > 0 ? parseCoef(typedK) / divisor : 0)
                       : typedSale !== undefined ? parseCoef(typedSale)
                       : appliedVente

    return {
      appliedFG, appliedVente, typedFg, typedSale, typedK,
      overheadCoef, saleCoef, kValue: divisor * saleCoef,
      fgDiffers:     Math.abs(overheadCoef - appliedFG)    > 0.0001,
      targetDiffers: Math.abs(saleCoef     - appliedVente) > 0.0001,
    }
  }

  // Overhead coef currently APPLIED for a category: quote override
  // if it exists, otherwise the account coef (preferences).
  appliedFGFor(category) {
    const overrides = this.props.quote?.overhead_coef_overrides || {}
    if (overrides[category] != null && overrides[category] !== "") {
      const n = typeof overrides[category] === "number"
        ? overrides[category]
        : parseFloat(String(overrides[category]).replace(",", "."))
      if (Number.isFinite(n)) return n
    }
    const accountCoefs = this.props.quote?.account?.overhead_coefs || {}
    const fallback     = parseCoef(this.props.quote?.account?.overhead_ratio) / 100
    return accountCoefs[category] != null ? parseCoef(accountCoefs[category]) : fallback
  }

  // Sale coef currently APPLIED: quote override if it exists,
  // otherwise sale coef derived from the current PV / applied cost price,
  // otherwise — nothing to read back — the account default.
  appliedVenteFor(category) {
    const overrides = this.props.quote?.sale_coef_overrides || {}
    if (overrides[category] != null && overrides[category] !== "") {
      const n = typeof overrides[category] === "number"
        ? overrides[category]
        : parseFloat(String(overrides[category]).replace(",", "."))
      if (Number.isFinite(n) && n > 0) return n
    }
    const buckets = computeBuckets(this.props.quote, enabledCategoriesFor(this.props.quote.account))
    const b = buckets[category]
    const revient = b ? b.purchase * (1 + this.appliedFGFor(category)) : 0
    if (b && b.purchase > 0 && revient > 0) return b.sale / revient
    // Category still empty: the coefficient that will apply is announced.
    const fromAccount = (this.props.quote?.account?.sale_coefs || {})[category]
    return fromAccount != null ? parseCoef(fromAccount) : 0
  }

  apply(category) {
    const { quote, replaceQuote } = this.props

    // selling price = purchase price × (1 + overhead coef) × sale coef
    const { overheadCoef, saleCoef: coefVente } = this.resolveFor(category)

    if (coefVente <= 0) {
      // Nothing to apply (no usable PV). We just clear the
      // state so the input falls back on the displayed applied value.
      this._clearStateFor(category)
      return
    }

    // 1) Recompute the unit_price of the category's qlines.
    // 2) Update the PERSISTED overrides on the quote (the applied
    //    value becomes the one displayed in the cost price
    //    columns + on all quote lines).
    // 3) Clear the local state ↦ the inputs revert to the applied
    //    value, fgDiffers/targetDiffers become FALSE again ↦ Apply
    //    button disabled.
    const newQlines = computeNewQlinesForCategory(quote, category, overheadCoef, coefVente)
    const newOverhead = { ...(quote.overhead_coef_overrides || {}), [category]: overheadCoef }
    const newSale     = { ...(quote.sale_coef_overrides     || {}), [category]: coefVente }
    replaceQuote({
      ...quote,
      qlines: newQlines,
      overhead_coef_overrides: newOverhead,
      sale_coef_overrides:     newSale,
    })
    this._clearStateFor(category)
  }

  _clearStateFor(category) {
    const nextFgTargets = { ...this.state.fgTargets }; delete nextFgTargets[category]
    const nextTargets   = { ...this.state.targets };   delete nextTargets[category]
    const nextKTargets  = { ...this.state.kTargets };  delete nextKTargets[category]
    this.setState({ targets: nextTargets, fgTargets: nextFgTargets, kTargets: nextKTargets })
  }

  render() {
    const { quote } = this.props
    const categories = enabledCategoriesFor(quote.account)
    const buckets   = computeBuckets(quote, categories)
    const totals    = totalsOf(buckets)
    const currency  = quote.currency_symbol || "€"
    // Cost price and the like ONLY use the APPLIED value
    // (override persisted on the quote, otherwise the account coef). As long as
    // the user hasn't clicked Apply, typing in the input doesn't
    // change any column — the update is deferred until the click.
    const fgFor        = category => buckets[category].purchase * this.appliedFGFor(category)
    const netProfit    = category => (buckets[category].sale - buckets[category].purchase) - fgFor(category)
    const fgTotal      = categories.reduce((s, c) => s + fgFor(c), 0)
    const netTotal     = (totals.sale - totals.purchase) - fgTotal
    const netPctTotal  = totals.sale > 0 ? (netTotal / totals.sale) * 100 : 0
    const kTotal       = totals.purchase > 0 ? totals.sale / totals.purchase : 0
    // The global sale coefficient bears on the cost price, overhead included.
    const revientTotal  = totals.purchase + fgTotal
    const saleCoefTotal = revientTotal > 0 ? totals.sale / revientTotal : 0

    const styles = {
      container: {
        marginBottom: '24px',
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden',
        boxSizing: 'border-box',
      },
      header: {
        padding: '16px 20px',
        background: 'linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%)',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      title: {
        fontSize: '15px',
        fontWeight: 600,
        color: '#111827',
        margin: 0,
      },
      totalBadge: {
        fontSize: '13px',
        color: '#374151',
        background: '#ffffff',
        padding: '6px 12px',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
      },
      tableWrap: { overflowX: 'auto' },
      table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        fontSize: '13px',
        minWidth: '1280px',
        fontVariantNumeric: 'tabular-nums',
      },
      th: {
        textAlign: 'left',
        padding: '10px 8px',
        background: '#f9fafb',
        color: '#6b7280',
        fontWeight: 600,
        textTransform: 'uppercase',
        fontSize: '11px',
        letterSpacing: '0.04em',
        borderBottom: '1px solid #e5e7eb',
        whiteSpace: 'nowrap',
      },
      thSticky: {
        position: 'sticky',
        left: 0,
        zIndex: 2,
        boxShadow: '1px 0 0 #e5e7eb',
      },
      thRight: { textAlign: 'right' },
      td: {
        padding: '12px 8px',
        borderBottom: '1px solid #f3f4f6',
        verticalAlign: 'middle',
      },
      tdSticky: {
        position: 'sticky',
        left: 0,
        zIndex: 1,
        background: '#ffffff',
        boxShadow: '1px 0 0 #e5e7eb',
      },
      tdRight: {
        padding: '12px 8px',
        borderBottom: '1px solid #f3f4f6',
        textAlign: 'right',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
      },
      categoryCell: { display: 'flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' },
      categoryDot: (color) => ({
        flex: '0 0 auto',
        width: '10px', height: '10px', borderRadius: '50%', background: color,
        boxShadow: `0 0 0 3px ${color}22`,
      }),
      categoryLabel: { fontWeight: 500, color: '#111827', whiteSpace: 'nowrap' },
      muted: { color: '#9ca3af' },
      marginPositive: { color: '#10b981', fontWeight: 600, whiteSpace: 'nowrap' },
      marginNegative: { color: '#ef4444', fontWeight: 600, whiteSpace: 'nowrap' },
      marginZero:     { color: '#6b7280', whiteSpace: 'nowrap' },
      marginPctSuffix: { marginLeft: 6, fontSize: '11px', fontWeight: 500 },
      input: {
        width: '64px',
        height: '28px',
        padding: '0 8px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '13px',
        textAlign: 'right',
        boxSizing: 'border-box',
        fontVariantNumeric: 'tabular-nums',
      },
      readOnlyCoef: {
        color: '#374151',
        fontWeight: 500,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      },
      coefCell: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        justifyContent: 'flex-end',
        whiteSpace: 'nowrap',
      },
      applyBtn: {
        height: '28px',
        padding: '0 10px',
        background: '#3b82f6',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
      },
      applyBtnDisabled: {
        height: '28px',
        padding: '0 10px',
        background: '#e5e7eb',
        color: '#9ca3af',
        border: 'none',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 500,
        cursor: 'not-allowed',
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
      },
      totalRow: { background: '#f9fafb', fontWeight: 600 },
      tdTotal: {
        padding: '14px 8px',
        borderTop: '2px solid #e5e7eb',
        verticalAlign: 'middle',
      },
      tdTotalSticky: {
        position: 'sticky',
        left: 0,
        zIndex: 1,
        background: '#f9fafb',
        boxShadow: '1px 0 0 #d1d5db',
      },
      tdTotalRight: {
        padding: '14px 8px',
        borderTop: '2px solid #e5e7eb',
        textAlign: 'right',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
      },
    }

    const headerCurrency = (
      <span style={{ fontWeight: 500, color: '#9ca3af', textTransform: 'none', letterSpacing: 0, marginLeft: 3 }}>
        ({currency})
      </span>
    )

    const renderMargin = (sale, purchase) => {
      const eur = sale - purchase
      const pct = marginPct(sale, purchase)
      const style =
        eur > 0 ? styles.marginPositive :
        eur < 0 ? styles.marginNegative : styles.marginZero
      return (
        <span style={style}>
          {formatNumber(eur)}
          <span style={{ marginLeft: 6, fontSize: '11px', fontWeight: 500 }}>({formatNumber(pct)} %)</span>
        </span>
      )
    }

    const renderNetMarginCell = (profit, sale) => {
      if (sale <= 0 && profit === 0) return <span style={styles.muted}>—</span>
      const pct = sale > 0 ? (profit / sale) * 100 : 0
      const style =
        profit > 0 ? styles.marginPositive :
        profit < 0 ? styles.marginNegative : styles.marginZero
      return (
        <span style={style}>
          {formatNumber(profit)}
          <span style={{ marginLeft: 6, fontSize: '11px', fontWeight: 500 }}>({formatNumber(pct)} %)</span>
        </span>
      )
    }

    const headerLabel  = I18n.t("quotes.cost_breakdown_table.net_margin_summary")
    const headerProfit = netTotal
    const headerPct    = netPctTotal

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h5 style={styles.title}>
            <i className="fas fa-chart-pie" style={{ marginRight: 8, color: '#6b7280' }}></i>
            {I18n.t("quotes.cost_breakdown_table.title")}
          </h5>
          <div style={styles.totalBadge}>
            {headerLabel} :
            <strong style={{ marginLeft: 6, color: headerPct >= 0 ? '#10b981' : '#ef4444' }}>
              {formatNumber(headerProfit)} {currency}
              <span style={{ marginLeft: 6, fontSize: '12px', fontWeight: 500 }}>({formatNumber(headerPct)} %)</span>
            </strong>
          </div>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.thSticky }}>{I18n.t("quotes.cost_breakdown_table.category")}</th>
                <th style={{ ...styles.th, ...styles.thRight }}>{I18n.t("quotes.cost_breakdown_table.lines")}</th>
                <th style={{ ...styles.th, ...styles.thRight }}>{I18n.t("quotes.cost_breakdown_table.hours")}</th>
                <th style={{ ...styles.th, ...styles.thRight }}>
                  {I18n.t("quotes.cost_breakdown_table.purchase_dry")}
                  {headerCurrency}
                </th>
                <th style={{ ...styles.th, ...styles.thRight }}>{I18n.t("quotes.cost_breakdown_table.overhead_coef")}</th>
                <th style={{ ...styles.th, ...styles.thRight }}>{I18n.t("quotes.cost_breakdown_table.sale_coef")}</th>
                {/* Label aligned LEFT so it sits directly above
                    the input (which occupies the left part of the cell)
                    and not above the Apply button on the right. */}
                <th style={{ ...styles.th, textAlign: 'left' }}>{I18n.t("quotes.cost_breakdown_table.k_coef")}</th>
                <th style={{ ...styles.th, ...styles.thRight }}>
                  {I18n.t("quotes.cost_breakdown_table.sale")}{headerCurrency}
                </th>
                <th style={{ ...styles.th, ...styles.thRight }}>
                  {I18n.t("quotes.cost_breakdown_table.margin")}{headerCurrency}
                </th>
                <th style={{ ...styles.th, ...styles.thRight }}>
                  {I18n.t("quotes.cost_breakdown_table.overhead")}{headerCurrency}
                </th>
                <th style={{ ...styles.th, ...styles.thRight }}>
                  {I18n.t("quotes.cost_breakdown_table.net_profit")}{headerCurrency}
                </th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => {
                const b               = buckets[category]
                const disabled        = b.count === 0 || b.purchase === 0

                const { appliedFG, appliedVente, saleCoef, kValue,
                        fgDiffers, targetDiffers } = this.resolveFor(category)

                const rawFg           = this.state.fgTargets[category]
                const rawTarget       = this.state.targets[category]
                const rawK            = this.state.kTargets[category]
                // All three at the thousandth: the line must multiply out on
                // screen.
                const fgInputValue    = rawFg     !== undefined ? rawFg     : (appliedFG !== 0 ? formatCoef(appliedFG) : "")
                const inputValue      = rawTarget !== undefined ? rawTarget : (saleCoef > 0 ? formatCoef(saleCoef) : "")
                const kInputValue     = rawK      !== undefined ? rawK      : (kValue   > 0 ? formatCoef(kValue)   : "")

                // The button activates as soon as at least one coef differs from its
                // APPLIED value. On click, the state is cleared: the inputs
                // fall back on the new applied value, fgDiffers and
                // targetDiffers become false again ↦ button disabled.
                // Sale coef must stay strictly positive; overhead coef
                // accepts negatives (discount / credit).
                const canApply        = !disabled
                                        && (targetDiffers || fgDiffers)
                                        && ((!targetDiffers) || saleCoef > 0)

                const profit          = netProfit(category)

                const coefAndApplyCell = (
                  <td style={{ ...styles.td, padding: '12px 8px', whiteSpace: 'nowrap' }}>
                    <div style={{ ...styles.coefCell, justifyContent: 'flex-start' }}>
                      <input
                        type        = "text"
                        inputMode   = "decimal"
                        placeholder = "2,00"
                        value       = {kInputValue}
                        disabled    = {disabled}
                        onChange    = {e => this.setK(category, e.target.value)}
                        style       = {{ ...styles.input, fontWeight: 600 }}
                        title       = {I18n.t("quotes.cost_breakdown_table.k_coef_tooltip")}
                      />
                      <button
                        type     = "button"
                        disabled = {!canApply}
                        onClick  = {() => this.apply(category)}
                        style    = {canApply ? styles.applyBtn : styles.applyBtnDisabled}
                        title    = {I18n.t("quotes.cost_breakdown_table.apply_tooltip")}
                      >
                        {I18n.t("quotes.cost_breakdown_table.apply")}
                      </button>
                    </div>
                  </td>
                )

                const saleInputCell = (
                  <td style={{ ...styles.td, padding: '12px 8px', whiteSpace: 'nowrap' }}>
                    <div style={{ ...styles.coefCell, justifyContent: 'flex-end' }}>
                      <input
                        type        = "text"
                        inputMode   = "decimal"
                        placeholder = "1,30"
                        value       = {inputValue}
                        disabled    = {disabled}
                        onChange    = {e => this.setTarget(category, e.target.value)}
                        style       = {styles.input}
                        title       = {I18n.t("quotes.cost_breakdown_table.sale_coef_tooltip")}
                      />
                    </div>
                  </td>
                )

                const fgInputCell = (
                  <td style={{ ...styles.td, padding: '12px 8px', whiteSpace: 'nowrap' }}>
                    <div style={{ ...styles.coefCell, justifyContent: 'flex-end' }}>
                      <input
                        type        = "text"
                        inputMode   = "decimal"
                        placeholder = "0,00"
                        value       = {fgInputValue}
                        disabled    = {disabled}
                        onChange    = {e => this.setFgTarget(category, e.target.value)}
                        style       = {styles.input}
                        title       = {I18n.t("quotes.cost_breakdown_table.overhead_coef_tooltip")}
                      />
                    </div>
                  </td>
                )

                return (
                  <tr key={category}>
                    <td style={{ ...styles.td, ...styles.tdSticky }}>
                      <div style={styles.categoryCell}>
                        <span style={styles.categoryDot(CATEGORY_COLORS[category])}></span>
                        <i className={CATEGORY_ICONS[category]} style={{ color: CATEGORY_COLORS[category], width: 16 }}></i>
                        <span style={styles.categoryLabel}>{I18n.t(`quotes.new.cost_category.${category}`)}</span>
                      </div>
                    </td>
                    <td style={styles.tdRight}>{b.count === 0 ? <span style={styles.muted}>—</span> : b.count}</td>
                    <td style={styles.tdRight}>{b.hours > 0 ? `${formatNumber(b.hours)} h` : <span style={styles.muted}>—</span>}</td>
                    <td style={styles.tdRight}>{b.purchase > 0 ? formatNumber(b.purchase) : <span style={styles.muted}>—</span>}</td>
                    {fgInputCell}
                    {saleInputCell}
                    {coefAndApplyCell}
                    <td style={styles.tdRight}>{b.sale > 0 ? formatNumber(b.sale) : <span style={styles.muted}>—</span>}</td>
                    <td style={styles.tdRight}>{b.sale > 0 || b.purchase > 0 ? renderMargin(b.sale, b.purchase) : <span style={styles.muted}>—</span>}</td>
                    <td style={styles.tdRight}>{b.purchase > 0 ? formatNumber(fgFor(category)) : <span style={styles.muted}>—</span>}</td>
                    <td style={styles.tdRight}>{(b.sale > 0 || b.purchase > 0) ? renderNetMarginCell(profit, b.sale) : <span style={styles.muted}>—</span>}</td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr style={styles.totalRow}>
                <td style={{ ...styles.tdTotal, ...styles.tdTotalSticky }}>{I18n.t("quotes.cost_breakdown_table.total")}</td>
                <td style={styles.tdTotalRight}>{totals.count || <span style={styles.muted}>—</span>}</td>
                <td style={styles.tdTotalRight}>{totals.hours > 0 ? `${formatNumber(totals.hours)} h` : <span style={styles.muted}>—</span>}</td>
                <td style={styles.tdTotalRight}>{formatNumber(totals.purchase)}</td>
                <td style={styles.tdTotalRight}>{totals.purchase > 0 ? formatNumber(fgTotal / totals.purchase) : <span style={styles.muted}>—</span>}</td>
                <td style={styles.tdTotalRight}>{saleCoefTotal > 0 ? `×${formatNumber(saleCoefTotal)}` : <span style={styles.muted}>—</span>}</td>
                <td style={styles.tdTotalRight}>{kTotal > 0 ? `×${formatCoef(kTotal)}` : <span style={styles.muted}>—</span>}</td>
                <td style={styles.tdTotalRight}>{formatNumber(totals.sale)}</td>
                <td style={styles.tdTotalRight}>{renderMargin(totals.sale, totals.purchase)}</td>
                <td style={styles.tdTotalRight}>{fgTotal > 0 ? formatNumber(fgTotal) : <span style={styles.muted}>—</span>}</td>
                <td style={styles.tdTotalRight}>{renderNetMarginCell(netTotal, totals.sale)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Hidden inputs — persistence of the overhead / sale coefs entered in the
            grid. One input per active category. The model
            Quote#normalize_coef_overrides discards empty values on the server side
            and converts "0,40" / "-0,15" into floats. */}
        {/* The hidden inputs reflect the APPLIED value (from the
            quote, not from the inputs' local state) — an entry
            not yet applied must NOT be persisted on save. */}
        {categories.map(cat => {
          const oh = (this.props.quote?.overhead_coef_overrides || {})[cat]
          const sl = (this.props.quote?.sale_coef_overrides     || {})[cat]
          const ohStr = (oh == null || oh === "") ? "" : String(oh)
          const slStr = (sl == null || sl === "") ? "" : String(sl)
          return (
            <React.Fragment key={`coef-hidden-${cat}`}>
              <input type="hidden" readOnly name={`quote[overhead_coef_overrides][${cat}]`} value={ohStr} />
              <input type="hidden" readOnly name={`quote[sale_coef_overrides][${cat}]`}     value={slStr} />
            </React.Fragment>
          )
        })}
      </div>
    )
  }
}
